'use client';

import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, ExternalLink, LogIn, UserPlus } from 'lucide-react';
import type {
  FinderOpportunity,
  FormAnswers,
  HouseholdMember,
  MyServeResponse,
  ServeForm,
} from '@/lib/types';
import { ResponseError, fetchForm, fetchHousehold, submitResponse } from '@/lib/api';
import { getMpWidgetToken, onMpTokenChange, signInAndReturn } from '@/lib/campus';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { Avatar } from './Avatar';
import { SheetBand } from './SheetBand';
import { FormFields, missingRequired } from './FormFields';

interface ResponseFormProps {
  apiBaseUrl: string;
  opportunity: FinderOpportunity;
  finderUrl: string;
  /** Household members who already responded to this opportunity are shown, not selectable */
  responded?: MyServeResponse[];
  /** Responses went out for these people */
  onSent?: (names: string[]) => void;
  /**
   * The details page's two steps: who in the card, the questions in a sheet.
   * The finder's sheet leaves this off and keeps the one-piece form.
   */
  stepped?: boolean;
  /** The page's accent custom properties: the sheet portals out of the page and would lose them */
  accentStyle?: React.CSSProperties;
  /** The line under the title in the sheet's band */
  bandSubtitle?: string | null;
  /** After sending, "Sign up for something else" calls this instead of linking to the finder */
  onAnother?: () => void;
  /**
   * A sheet's pinned footer. The send button is drawn there (tied to the form
   * by id) so it never scrolls out of reach, as on an event's registration.
   */
  submitSlot?: HTMLElement | null;
}

const FIELD =
  'w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[var(--accent,#299c8f)] focus:ring-2 focus:ring-[color-mix(in_srgb,var(--accent,#299c8f)_30%,transparent)] focus:outline-none';
const LABEL = 'text-xs font-bold tracking-widest text-gray-500 uppercase';
const CTA =
  'inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 text-base font-bold text-[var(--on-accent,#fff)] shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100';

const MAX_PEOPLE = 10;

function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Where a signed-out visitor's draft waits while they sign in. Session
 * storage survives MP's hosted sign-in round trip (same tab) and is gone
 * when the tab closes. `pendingSend` means they pressed the button: once
 * the token lands the response goes out, no second click.
 */
interface Draft {
  comments: string;
  selected: number[];
  answers: FormAnswers;
  pendingSend: boolean;
  at: number;
}
const DRAFT_MAX_AGE_MS = 30 * 60 * 1000;
const draftKey = (id: number) => `serve-finder:response-draft:${id}`;

function readDraft(id: number): Draft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(id));
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<Draft>;
    if (!d.at || Date.now() - d.at > DRAFT_MAX_AGE_MS) {
      sessionStorage.removeItem(draftKey(id));
      return null;
    }
    return {
      comments: d.comments ?? '',
      selected: Array.isArray(d.selected) ? d.selected : [],
      answers: d.answers && typeof d.answers === 'object' ? d.answers : {},
      pendingSend: Boolean(d.pendingSend),
      at: d.at,
    };
  } catch {
    return null;
  }
}
function writeDraft(id: number, d: Draft) {
  try {
    sessionStorage.setItem(draftKey(id), JSON.stringify(d));
  } catch {
    /* storage blocked: they will retype */
  }
}
function clearDraft(id: number) {
  try {
    sessionStorage.removeItem(draftKey(id));
  } catch {
    /* nothing to clear */
  }
}

/**
 * "Count me in." Signed in, pick anyone in the household (several at once,
 * or one when the opportunity has a form, since a form asks about one
 * person), answer the form if there is one, add a note, send. Signed out,
 * the form looks like the start of the sign-up: an "Add yourself" chip
 * where the household would be, and pressing it (or the button) goes
 * through the host page's `<mpp-user-login>` modal or MP's hosted sign-in
 * with a round trip; the draft waits in sessionStorage and goes out the
 * moment the token lands.
 */
export function ResponseForm({
  apiBaseUrl,
  opportunity,
  finderUrl,
  responded = [],
  onSent,
  stepped = false,
  accentStyle,
  bandSubtitle,
  onAnother,
  submitSlot,
}: ResponseFormProps) {
  const formId = `sf-response-${opportunity.id}`;
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const [token, setToken] = useState<string | null>(() => getMpWidgetToken());
  const [members, setMembers] = useState<HouseholdMember[] | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [form, setForm] = useState<ServeForm | null | undefined>(
    opportunity.customFormId ? undefined : null
  );
  const [formError, setFormError] = useState(false);
  const [draft] = useState<Draft | null>(() => readDraft(opportunity.id));
  const [selected, setSelected] = useState<number[]>(draft?.selected ?? []);
  const [answers, setAnswers] = useState<FormAnswers>(draft?.answers ?? {});
  const [comments, setComments] = useState(draft?.comments ?? '');
  const [pendingSend, setPendingSend] = useState(Boolean(draft?.pendingSend));
  const [website, setWebsite] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ duplicate: boolean; names: string[] } | null>(null);

  const pendingRef = useRef(pendingSend);
  pendingRef.current = pendingSend;
  const singlePerson = opportunity.customFormId !== null;

  useEffect(() => onMpTokenChange(setToken), []);

  // The custom form, when the opportunity has one.
  useEffect(() => {
    if (!opportunity.customFormId) return;
    const controller = new AbortController();
    fetchForm(apiBaseUrl, opportunity.id, controller.signal)
      .then((f) => {
        if (!controller.signal.aborted) setForm(f);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFormError(true);
      });
    return () => controller.abort();
  }, [apiBaseUrl, opportunity.id, opportunity.customFormId]);

  useEffect(() => {
    if (!token) {
      setMembers(null);
      return;
    }
    const controller = new AbortController();
    setMembersLoading(true);
    fetchHousehold(apiBaseUrl, controller.signal)
      .then((m) => {
        if (controller.signal.aborted) return;
        setMembers(m);
        // A serve sign-up is usually for yourself: start there, unless the
        // draft already says who.
        const self = m?.find((x) => x.isSelf)?.contactId;
        if (self !== undefined) setSelected((cur) => (cur.length ? cur : [self]));
      })
      .finally(() => {
        if (!controller.signal.aborted) setMembersLoading(false);
      });
    return () => controller.abort();
  }, [apiBaseUrl, token]);

  const selfMember = members?.find((m) => m.isSelf) ?? null;
  // Already responded (and still open): shown with a check, not selectable.
  const respondedIds = useMemo(
    () =>
      new Set(
        responded
          .filter((r) => r.status === 'pending' || r.status === 'placed')
          .map((r) => r.contactId)
      ),
    [responded]
  );
  const chosen = useMemo(
    () =>
      (members ?? []).filter(
        (m) => selected.includes(m.contactId) && !respondedIds.has(m.contactId)
      ),
    [members, selected, respondedIds]
  );
  const count = token ? chosen.length : 1;
  const missing = form ? missingRequired(form, answers) : [];
  const formReady = form === null || (form !== undefined && form.supported && missing.length === 0);
  const canSend =
    count > 0 &&
    count <= MAX_PEOPLE &&
    (token ? members !== null && formReady : form !== undefined);

  const toggleMember = (contactId: number) =>
    setSelected((cur) => {
      if (singlePerson) return cur.includes(contactId) ? [] : [contactId];
      return cur.includes(contactId) ? cur.filter((id) => id !== contactId) : [...cur, contactId];
    });

  // Hooks stay above the early returns below, or React sees a different
  // hook count once the form flips to its sent state.
  const send = async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const result = await submitResponse(apiBaseUrl, opportunity.id, {
        people: chosen.map((m) => ({ contactId: m.contactId })),
        comments: comments.trim(),
        answers: form ? answers : undefined,
        website,
      });
      clearDraft(opportunity.id);
      setPendingSend(false);
      const names = result.results?.length
        ? result.results.map((r) => r.name)
        : chosen.map((m) => m.name);
      setSent({ duplicate: Boolean(result.duplicate), names });
      setQuestionsOpen(false);
      setSelected([]);
      onSent?.(names);
    } catch (err) {
      setPendingSend(false);
      // The sign-in ran out and MP would not renew it: sign in and send on return.
      if (err instanceof ResponseError && err.status === 401) {
        setToken(null);
        startSignIn(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not send your response');
      }
    } finally {
      setSending(false);
    }
  };

  const startSignIn = (andSend: boolean) => {
    writeDraft(opportunity.id, {
      comments,
      selected,
      answers,
      pendingSend: andSend,
      at: Date.now(),
    });
    if (andSend) setPendingSend(true);
    signInAndReturn();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    if (!token) {
      startSignIn(true);
      return;
    }
    void send();
  };

  // They pressed the button before signing in. The token is here now and
  // their household has loaded: finish what they started.
  useEffect(() => {
    if (!pendingSend || !token || sending || sent) return;
    if (members === null || chosen.length === 0 || !formReady) return;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSend, token, members, chosen.length, formReady]);

  if (opportunity.externalRegistrationUrl) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-base font-bold text-[#1c2b39]">
          Sign-ups for this one happen on another site.
        </p>
        <a
          href={opportunity.externalRegistrationUrl}
          target="_blank"
          rel="noopener"
          className={`${CTA} bg-[var(--accent,#299c8f)]`}
        >
          Sign up
          <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
        </a>
      </div>
    );
  }

  if (form !== null && form !== undefined && !form.supported) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-base font-bold text-[#1c2b39]">
          This sign-up needs a form we can't show here yet.
        </p>
        <p className="text-sm text-gray-600">
          {opportunity.contact
            ? `Reach out to ${opportunity.contact.name} and they'll get you signed up.`
            : 'Reach out to the campus office and they will get you signed up.'}
        </p>
      </div>
    );
  }

  if (sent && !stepped) {
    const onlySelf =
      sent.names.length === 1 && selfMember !== null && sent.names[0] === selfMember.name;
    const plural = sent.names.length > 1;
    const who = opportunity.contact?.name ?? 'the team';
    return (
      <div className="flex flex-col items-start gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]">
          <Check className="h-6 w-6" strokeWidth={3} />
        </span>
        <p className="text-base font-bold text-[#1c2b39]">
          {sent.duplicate
            ? `${who === 'the team' ? 'The team' : who} already has your ${plural ? 'sign-ups' : 'sign-up'}.`
            : plural
              ? "You're on the list."
              : onlySelf
                ? "You're on the list."
                : `${sent.names[0]} is on the list.`}
        </p>
        <p className="text-sm text-gray-600">
          {onlySelf
            ? `We let ${who} know you're interested, and they'll reach out soon.`
            : `We let ${who} know that ${listNames(sent.names)} ${plural ? 'are' : 'is'} interested, and they'll be in touch soon.`}
          {opportunity.hasResponseMessage && selfMember?.email
            ? ` A note is on its way to ${selfMember.email}.`
            : ''}
        </p>
        {onAnother ? (
          <button
            type="button"
            onClick={onAnother}
            className="text-sm font-semibold text-[var(--accent-text,var(--accent,#299c8f))] hover:underline"
          >
            Sign up for something else
          </button>
        ) : (
          <a
            href={finderUrl}
            className="text-sm font-semibold text-[var(--accent-text,var(--accent,#299c8f))] hover:underline"
          >
            Find another place to serve
          </a>
        )}
      </div>
    );
  }

  const contactFirst = opportunity.contact?.name.split(' ')[0] ?? null;

  const picker = (
    <div className="flex flex-col gap-3">
      {stepped && sent && (
        <p className="flex items-start gap-2.5 rounded-2xl bg-[var(--accent-soft,#e6f4f2)] px-3.5 py-3 text-sm text-[#1c2b39]">
          <Check
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent-text,var(--accent,#299c8f))]"
            strokeWidth={3}
          />
          <span>
            {sent.duplicate
              ? `${opportunity.contact?.name ?? 'The team'} already has ${sent.names.length > 1 ? 'these sign-ups' : 'this sign-up'}.`
              : `${listNames(sent.names)} ${sent.names.length > 1 ? 'are' : 'is'} on the list. ${opportunity.contact?.name ?? 'The team'} will reach out.`}
          </span>
        </p>
      )}
      {token && membersLoading && !members ? (
        <div className="h-12 w-full max-w-sm animate-pulse rounded-full bg-gray-100" />
      ) : token && members && members.length === 0 ? (
        <p className="text-sm text-gray-500">
          We couldn't load your profile. Try signing in again.
        </p>
      ) : null}
      {(!token || members) && (
        /*
          The event sign-up's list, row for row: a square checkbox in the
          page's colour, the photo, the name, and what they are to you. One
          question ("who is coming") answered the same way everywhere.
        */
        <ul className="-my-1 divide-y divide-gray-100">
          {!token && (
            <li>
              <button
                type="button"
                onClick={() => startSignIn(false)}
                className="flex w-full items-center gap-3 py-3 text-left"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white" />
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-500">
                  <UserPlus className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <span className="text-sm font-bold text-[#1c2b39]">Add yourself</span>
              </button>
            </li>
          )}
          {(members ?? []).map((m) => {
            const done = respondedIds.has(m.contactId);
            const on = !done && selected.includes(m.contactId);
            const relation = m.isSelf ? 'You' : (m.relationship ?? null);
            return (
              <li key={m.contactId}>
                <button
                  type="button"
                  onClick={() => !done && toggleMember(m.contactId)}
                  aria-pressed={on}
                  disabled={done}
                  className="flex w-full items-start gap-3 py-3 text-left disabled:cursor-default"
                >
                  <span
                    className={`mt-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                      on
                        ? 'border-[var(--accent,#299c8f)] bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]'
                        : done
                          ? 'border-gray-200 bg-gray-100'
                          : 'border-gray-300 bg-white'
                    }`}
                  >
                    {(on || done) && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </span>
                  <Avatar name={m.name} imageUrl={m.imageUrl} size={36} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-1.5">
                      <span className="truncate text-sm font-bold text-[#1c2b39]">{m.name}</span>
                      {relation && (
                        <span className="shrink-0 text-[0.7rem] font-semibold text-gray-500">
                          {relation}
                        </span>
                      )}
                    </span>
                    {done && (
                      <span className="mt-1 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[0.65rem] font-bold text-gray-600">
                        Signed up
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const sendButton = (
    <button
      type="submit"
      form={formId}
      disabled={!canSend || sending}
      className={`${CTA} bg-[var(--accent,#299c8f)] ${submitSlot ? 'w-full' : ''}`}
    >
      {sending
        ? 'Sending…'
        : !token
          ? 'Sign in and sign up'
          : count > 1
            ? `Sign up ${count} people`
            : 'Sign up'}
      {!sending && !token && <LogIn className="h-4 w-4" strokeWidth={2.5} />}
      {!sending && token && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
    </button>
  );

  const questions = (
    <>
      {form === undefined && !formError && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <div className="h-3 w-40 animate-pulse rounded-full bg-gray-100" />
          <div className="h-12 w-full animate-pulse rounded-2xl bg-gray-100" />
          <div className="h-12 w-full animate-pulse rounded-2xl bg-gray-100" />
        </div>
      )}
      {formError && (
        <p className="text-sm text-gray-500">
          We couldn't load this sign-up's questions. Try again in a moment.
        </p>
      )}
      {form && <FormFields form={form} answers={answers} onChange={setAnswers} />}

      <label className="flex flex-col gap-2">
        <span className={LABEL}>
          Anything {contactFirst ?? 'the team'} should know?{' '}
          <span className="font-normal tracking-normal text-gray-400 normal-case">Optional</span>
        </span>
        <textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Availability, experience, questions."
          className={FIELD}
        />
      </label>

      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      {error && <p className="text-sm font-semibold text-red-600">{error}</p>}

      <div className="flex flex-wrap items-center gap-3">
        {submitSlot ? null : sendButton}
        {token && count === 0 && members && members.length > 0 && (
          <span className="text-xs text-gray-500">Pick at least one person.</span>
        )}
        {token && count > 0 && form && missing.length > 0 && (
          <span className="text-xs text-gray-500">
            {missing.length === 1
              ? `"${missing[0].label}" still needs an answer.`
              : `${missing.length} questions still need an answer.`}
          </span>
        )}
      </div>
    </>
  );

  if (!stepped) {
    return (
      <form id={formId} onSubmit={submit} className="flex flex-col gap-6">
        {picker}
        {questions}
        {submitSlot && createPortal(sendButton, submitSlot)}
      </form>
    );
  }

  /*
    STEPPED, the way an event does it: the card holds who is coming and a
    Next button; the questions, the note and Send open in a sheet wearing the
    page's band. Same state, same draft and same sign-in round trip as the
    one-piece form, so nothing about sending changes, only where it is asked.
  */
  const nextReady = !token || (members !== null && count > 0 && count <= MAX_PEOPLE);

  // No custom form, nothing to ask: the card signs up on its own, no sheet.
  if (!opportunity.customFormId) {
    return (
      <form onSubmit={submit} className="flex flex-col gap-5">
        {picker}
        {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={!canSend || sending}
          className={`${CTA} w-full bg-[var(--accent,#299c8f)]`}
        >
          {sending
            ? 'Sending…'
            : !token
              ? 'Sign in and sign up'
              : count > 1
                ? `Sign up ${count} people`
                : 'Sign up'}
          {!sending && !token && <LogIn className="h-4 w-4" strokeWidth={2.5} />}
          {!sending && token && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
        </button>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {picker}
      <button
        type="button"
        disabled={!nextReady}
        onClick={() => setQuestionsOpen(true)}
        className={`${CTA} w-full bg-[var(--accent,#299c8f)]`}
      >
        Next
        <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
      </button>
      <ResponsiveSheet
        open={questionsOpen}
        onClose={() => setQuestionsOpen(false)}
        maxWidth="max-w-xl"
        noPanelPadding
        panelClassName="bg-white"
        hideCloseButton
        header={() => (
          <div style={accentStyle}>
            <SheetBand
              opportunity={opportunity}
              title={opportunity.title}
              subtitle={bandSubtitle}
              onClose={() => setQuestionsOpen(false)}
            />
          </div>
        )}
      >
        <SheetPage name="main">
          <form
            id={formId}
            onSubmit={submit}
            className="flex flex-col gap-6 p-5 text-[#1c2b39] sm:p-6"
            style={accentStyle}
          >
            {questions}
          </form>
        </SheetPage>
      </ResponsiveSheet>
    </div>
  );
}
